import Async from "components/DesignSystem/AsyncComponents/Async";
import Card from "components/DesignSystem/Card/Card";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import { cashFlowPeriod, monthPeriod } from "constants/bookkeeping";
import { CATEGORISATION_TYPE } from "constants/booksHomePage";
import {
  EXPENSE_CATEGORY_IDENTIFIER,
  REVENUE_CATEGORY_IDENTIFIER,
} from "constants/categoryIdentifier";
import { YYYY_MM_DD } from "constants/date";
import * as DATE_PERIOD from "constants/dateFilter";
import dayjs from "dayjs";
import { useChartOfAccounts } from "hooks/useChartOfAccounts";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useFilters } from "hooks/useFilter";
import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { MultiValue, SingleValue } from "react-select";
import { useHistory } from "react-router-dom";
import { useGetCategoryByIdentifierQuery } from "store/apis/chartOfAccounts";
import {
  useGetMetricsQuery,
  useGetRevenueMetricsQuery,
} from "store/apis/metrics";
import { ForecastQueryParams } from "store/apis/financialMetrics";
import { useGetBookkeepingSettingsQuery } from "store/apis/booksLockDate";
import { setLedgerFilter } from "store/slices/generalLedger";
import { flattenTypes } from "utils/flattenCOA";
import { DateRangeValue, getDateRange } from "utils/getDateRange";
import { HomePageCardHeader } from "../HomePageCardHeader";
import { CashFlowChart, MetricsReport } from "./CashFlowChart";
import { CashBalanceIcon } from "components/icons/CashBalanceIcon";
import { Spinner } from "components/DesignSystem/Loader/loader";
import { useConstructInternalLink } from "hooks/useConstructInternalLink";
export type FilterType = {
  PERIOD: DateRangeValue;
};

type PeriodProp = {
  startDate: string;
  endDate: string;
  periodText?: string;
  isCustomDateRange?: boolean;
};

export const CashFlow = ({
  categorisationType,
  defaultPeriod = DATE_PERIOD.LAST_6_MONTHS,
  period,
  groupId: propsGroupId,
  assumption_set_id,
  forecast_start_date,
  forecast_end_date,
}: {
  categorisationType: keyof typeof CATEGORISATION_TYPE;
  withCard?: boolean;
  defaultPeriod?:
    | typeof DATE_PERIOD.LAST_6_MONTHS
    | typeof DATE_PERIOD.LAST_12_MONTHS;
  period?: PeriodProp;
  groupId?: string;
} & ForecastQueryParams) => {
  const { uuid: contextGroupId } = useCurrentGroupContext();
  const groupId = propsGroupId || contextGroupId;
  const entityId = useCurrentEntityId();
  const dispatch = useDispatch();
  const history = useHistory();
  const { values, updateFilter } = useFilters<FilterType>({
    initialValue: { PERIOD: defaultPeriod },
  });
  const { data: revenueCategory } = useGetCategoryByIdentifierQuery(
    {
      groupId,
      entityId,
      identifiers: [REVENUE_CATEGORY_IDENTIFIER],
    },
    { skip: !groupId || !entityId }
  );

  const { data: expenseCategory } = useGetCategoryByIdentifierQuery(
    {
      groupId,
      entityId,
      identifiers: [EXPENSE_CATEGORY_IDENTIFIER],
    },
    { skip: !groupId || !entityId }
  );

  const revenueRootUUID = Array.isArray(revenueCategory)
    ? revenueCategory.map((c) => c.uuid).join(",")
    : "";

  const expenseRootUUID = Array.isArray(expenseCategory)
    ? expenseCategory.map((c) => c.uuid).join(",")
    : "";

  const { chartOfAccounts: revenueAccounts } = useChartOfAccounts({
    root_category_id: revenueRootUUID,
  });
  const { chartOfAccounts: expenseAccounts } = useChartOfAccounts({
    root_category_id: expenseRootUUID,
  });
  const { link } = useConstructInternalLink();

  const { data: bookkeepingSettings } = useGetBookkeepingSettingsQuery(
    { entityId },
    { skip: !entityId }
  );

  const handleAmountClick = useCallback(
    (type: "revenue" | "expense", date: string) => {
      const accounts = type === "revenue" ? revenueAccounts : expenseAccounts;
      const categoryIds = flattenTypes({ accounts }).map((t) => t.uuid);
      if (!categoryIds.length) return;

      const monthStart = dayjs(date).startOf("month").format(YYYY_MM_DD);
      const monthEnd = dayjs(date).endOf("month").format(YYYY_MM_DD);
      const helper = link(`/books/categories`, {
        moreQuery: {
          startDate: monthStart,
          endDate: monthEnd,
        },
      });

      dispatch(
        setLedgerFilter({
          categoryIds,
          ...(bookkeepingSettings?.accounting_method && {
            accountingMethod: bookkeepingSettings.accounting_method,
          }),
        })
      );

      history.push(helper);
    },
    [
      revenueAccounts,
      expenseAccounts,
      entityId,
      dispatch,
      history,
      bookkeepingSettings,
    ]
  );

  const internalDateRange = getDateRange(values.PERIOD);
  const startDate =
    period?.startDate ?? dayjs(internalDateRange.startDate).format(YYYY_MM_DD);
  const endDate =
    period?.endDate ?? dayjs(internalDateRange.endDate).format(YYYY_MM_DD);
  const value = monthPeriod.find((item) => item.value === values.PERIOD);

  const { data = [], ...revenueMetricsApiStates } = useGetRevenueMetricsQuery(
    {
      groupId,
      entityId,
      start_date: startDate,
      end_date: endDate,
      categorisation_type: categorisationType,
      assumption_set_id,
      forecast_start_date,
      forecast_end_date,
    },
    {
      skip:
        !groupId || !entityId || !categorisationType || !startDate || !endDate,
      refetchOnMountOrArgChange: 60, // Cache for 60 seconds
    }
  );

  const { data: metrics, ...metricsApiStates } = useGetMetricsQuery(
    {
      entityId,
      burn_rate_start_date: startDate,
      burn_rate_end_date: endDate,
      categorisation_type: categorisationType,
      is_custom_date_range: period?.isCustomDateRange || undefined,
    },
    {
      skip: !entityId || !categorisationType || !startDate || !endDate,
      refetchOnMountOrArgChange: 60, // Cache for 60 seconds
    }
  );

  const isFetching =
    revenueMetricsApiStates.isFetching || metricsApiStates.isFetching;
  const isSuccess =
    revenueMetricsApiStates.isSuccess && metricsApiStates.isSuccess;
  const isError = revenueMetricsApiStates.isError || metricsApiStates.isError;

  const forecastEntries = data.filter((item) => item.is_forecast);
  const forecastTotals = forecastEntries.length
    ? forecastEntries.reduce(
        (totals, item) => ({
          revenue: totals.revenue + item.revenue,
          expense: totals.expense + item.expense,
          profit: totals.profit + item.profit,
        }),
        { revenue: 0, expense: 0, profit: 0 }
      )
    : undefined;

  return (
    <Card.Root borderRounded="lg" className="sm:t-px-4">
      <HomePageCardHeader>
        <span className="t-text-subtitle-lg t-text-text-black">
          Profitability
        </span>
        {!period && (
          <Combobox
            isSearchable={false}
            onChange={(
              value: SingleValue<OptionData> | MultiValue<OptionData>
            ) => {
              if (value instanceof Array) {
                return;
              }

              const range = (value as SingleValue<OptionData>)?.value!;

              if (range) {
                updateFilter("PERIOD", range as DateRangeValue);
              }
            }}
            size="xsmall"
            menuPortalTarget={document.body}
            options={cashFlowPeriod}
            isClearable={false}
            defaultValue={value}
          />
        )}
      </HomePageCardHeader>
      <Card.Body className="t-flex t-flex-col t-gap-3">
        <Async.Root
          isLoading={isFetching}
          isSuccess={isSuccess}
          isEmpty={
            data.length === 0 ||
            data.every(
              (item) =>
                item.revenue === 0 && item.expense === 0 && item.profit === 0
            )
          }
          isError={isError}
          customLoader={
            <div className="t-flex t-flex-col t-justify-center t-items-center t-h-72 t-gap-3">
              <Spinner />
              <span className="t-text-subtext-sm t-text-text-30">
                Loading Profitability...
              </span>
            </div>
          }
        >
          <Async.Empty>
            <div className="t-flex t-justify-center t-items-center t-h-72  t-text-text-30 t-flex-col">
              <CashBalanceIcon />
              <span className="t-pt-4 t-text-body-sm">
                No profitability data yet
              </span>
              <span className="t-text-body-sm t-mt-2 t-items-center t-text-center">
                Connect your bank accounts to see how revenue, costs, and profit
                trend over time
              </span>
            </div>
          </Async.Empty>
          <Async.ErrorHandler>
            <div className="t-flex t-justify-center t-items-center t-h-40 t-text-red-50">
              Something went wrong!
            </div>
          </Async.ErrorHandler>
          <Async.Success>
            {metrics && (
              <MetricsReport
                data={metrics}
                periodText={
                  period?.periodText ??
                  value?.label?.toLowerCase().replace("last ", "")
                }
                forecastTotals={forecastTotals}
              />
            )}
            <CashFlowChart data={data} onAmountClick={handleAmountClick} />
          </Async.Success>
        </Async.Root>
      </Card.Body>
    </Card.Root>
  );
};
